import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { styleHelpers } from '@leroy-merlin-br/backyard-react'

import type { Theme } from 'scripts/react-components/themes/Theme'

type StyleProps = {
  isPromotional: boolean
  isPromotionalByPaymentMethod: boolean
  isOutlet: boolean
  isUnavailable: boolean
}

const baseStyles = ({ theme }: Theme) => css`
  label: price-tag;

  display: flex;
  flex-direction: column;

  background-color: ${theme.colors.white};
  border-top: 4px solid ${theme.colors.p600};
`

const promotionalStyles = ({ theme, isPromotional }: Theme & StyleProps) =>
  isPromotional &&
  css`
    label: price-tag--promotional;

    border: none;
    padding-bottom: ${theme.spacings.bit};

    & ${Price} {
      label: price-tag__price--promotional;

      color: ${theme.colors.r600};
      background-color: ${theme.colors.y500};
      padding: ${theme.spacings.kilo} 0 ${theme.spacings.kilo}
        ${theme.spacings.mega};
      margin-bottom: ${theme.spacings.byte};
    }
  `

const promotionalPaymentMethodStyles = ({
  theme,
  isPromotionalByPaymentMethod
}: Theme & StyleProps) =>
  isPromotionalByPaymentMethod &&
  css`
    label: price-tag--promotional-payment-method;

    border: none;
    padding-bottom: ${theme.spacings.bit};

    & ${Price} {
      label: price-tag__price--promotional-payment-method;

      background-color: ${theme.colors.p100};
      padding: ${theme.spacings.kilo} 0 ${theme.spacings.kilo}
        ${theme.spacings.mega};
      margin-bottom: ${theme.spacings.byte};
    }
  `

const outletStyles = ({ theme, isOutlet }: Theme & StyleProps) =>
  isOutlet &&
  css`
    label: price-tag--black-weekend;

    border: none;
    padding-bottom: ${theme.spacings.bit};

    & ${Price} {
      label: price-tag__price--black-weekend;

      color: ${theme.colors.p500};
      background-color: ${theme.colors.black};
      padding: ${theme.spacings.kilo} 0 ${theme.spacings.kilo}
        ${theme.spacings.mega};
      margin-bottom: ${theme.spacings.byte};
    }
  `

const unavailableStyles = ({ theme, isUnavailable }: Theme & StyleProps) =>
  isUnavailable &&
  css`
    label: price-tag--sold-out;

    border-color: ${theme.colors.n600};
  `

const priceStyles = ({ theme }: Theme) => css`
  label: price-tag__price;

  display: flex;
  flex-direction: column;

  width: 100%;
  ${styleHelpers.headingTera(theme)};
  font-weight: ${theme.font.weight.bold};
`

const totalPriceStyles = ({ theme }: Theme) => css`
  label: price-tag__total-price;

  ${styleHelpers.headingExa(theme)};
`

const packagingPriceStyles = ({ theme }: Theme) => css`
  label: price-tag__packaging-price;

  width: 100%;
  margin: ${theme.spacings.byte} 0;
  padding: ${theme.spacings.byte} ${theme.spacings.kilo};
  background: ${theme.colors.p200};
`

const dotStyles = ({ theme }: Theme) => css`
  label: price-tag__packaging-price-dot;

  &::after {
    content: '';

    display: inline-block;
    vertical-align: middle;

    width: ${theme.spacings.bit};
    height: ${theme.spacings.bit};
    margin: 0 ${theme.spacings.byte};

    border-radius: ${theme.borderRadius.circle};
    background-color: ${theme.colors.n900};
  }
`

const titleStyles = ({ theme }: Theme) => css`
  label: price-tag__title;

  ${styleHelpers.headingTera(theme)};
  color: ${theme.colors.n600};
`

export const PriceTag = styled('div')(
  baseStyles,
  promotionalStyles,
  promotionalPaymentMethodStyles,
  outletStyles,
  unavailableStyles
)
export const Price = styled('div')(priceStyles)
export const TotalPrice = styled('span')(totalPriceStyles)
export const PackagingPrice = styled('div')(packagingPriceStyles)
export const Dot = styled('span')(dotStyles)
export const Title = styled('div')(titleStyles)
